import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppAuthProvider } from './components/Auth/AuthProvider';
import AuthGuard from './components/Auth/AuthGuard';
import Layout from './components/Layout';
import { LoadingSpinner } from './components/Common/LoadingSpinner';
import LoginPage from './components/Auth/LoginPage';
import ConstructionListPage from './pages/Construction';
import ConstructionFormPage from './pages/Construction/FormPage';
import DailyReportListPage from './pages/DailyReport';
import DailyReportFormPage from './pages/DailyReport/FormPage';
import SchedulePage from './pages/SchedulePage';
import { AuthErrorBoundary } from './components/Auth/AuthError';
import CalculationSheetPage from './pages/CalculationSheet/CalculationSheet';
import InvoiceRequestListPage from './pages/InvoiceRequest';
import SalesPage from './pages/Sales'; // 売上管理ページをインポート
import CustomerSearchPage from './pages/Customers';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';

// React Queryのインポート
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// ReportContextのインポート
import { ReportProvider } from './contexts/ReportContext';


// カスタムテーマを作成 - デスクトップタッチスクリーン向けに最適化
const theme = createTheme({
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        html {
          WebkitTapHighlightColor: transparent;
          touchAction: manipulation;
        }
        body {
          touchAction: pan-x pan-y;
          WebkitOverflowScrolling: touch;
          overscrollBehavior: none;
        }
        #root {
          height: 100%;
          overflowY: auto;
          WebkitOverflowScrolling: touch;
        }
        
        /* デスクトップタッチスクリーン向けの最適化 */
        @media (min-width: 1024px) {
          html, body, #root {
            scrollBehavior: smooth;
            touch-action: pan-y !important;
            -webkit-overflow-scrolling: touch !important;
          }
          
          /* コンテンツ要素のスクロール設定 */
          .MuiContainer-root, .MuiBox-root, .MuiPaper-root {
            overflowY: auto !important;
            overflowX: hidden;
            WebkitOverflowScrolling: touch !important;
            touchAction: pan-y !important;
          }
          
          /* タッチ操作のターゲットエリアを拡大 */
          button, a, [role="button"] {
            min-height: 32px;
            min-width: 32px;
          }
          
          /* タッチフィードバックの改善 */
          button:active, a:active, [role="button"]:active {
            transform: scale(0.98);
          }
        }
        
        /* Microsoft Surface等の大型タッチスクリーン向け特別設定 */
        @media (min-width: 1366px) and (pointer: coarse) {
          .MuiContainer-root {
            padding-bottom: 50px !important;
          }
          
          input, select, textarea {
            font-size: 16px !important;
          }
        }
      `
    }
  }
});


// QueryClientの作成
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15 * 60 * 1000, // 15分間はデータをfreshとみなす
      gcTime: 30 * 60 * 1000, // 30分間はキャッシュを保持
      refetchOnWindowFocus: false, // ウィンドウフォーカス時に再取得しない
    },
  },
});

function App() {
  // デスクトップタッチスクリーン対応のためのイベントリスナー
  useEffect(() => {
    // タッチデバイスを検出
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    if (isTouchDevice) {
      // タッチデバイス向けにクラスを追加
      document.body.classList.add('touch-device');
      
      // ポインターイベントのパフォーマンス最適化
      const passiveSupported = (() => {
        let passive = false;
        try {
          const options = Object.defineProperty({}, 'passive', {
            get: function() { passive = true; return true; }
          });
          window.addEventListener('test', null as any, options);
          window.removeEventListener('test', null as any, options);
        } catch(err) { /* 何もしない */ }
        return passive;
      })();
      
      // パッシブモードをサポートする場合はそれを使用
      const wheelOpt = passiveSupported ? { passive: true } : false;
      
      // デスクトップタッチデバイスでのスクロール最適化
      const preventDefaultForScrollKeys = (e: KeyboardEvent) => {
        // スペースやページキーなどのスクロールキーを許可
        const keys = { 32: 1, 33: 1, 34: 1, 35: 1, 36: 1, 37: 1, 38: 1, 39: 1, 40: 1 };
        if (keys[e.keyCode as keyof typeof keys]) {
          return true; // 通常のスクロール動作を許可
        }
      };
      
      // ホイールイベントをスムーズに
      window.addEventListener('wheel', () => {}, wheelOpt);
      // タッチデバイスでのピンチズームを許可しつつスクロールも確保
      window.addEventListener('touchstart', () => {}, wheelOpt);
      window.addEventListener('touchmove', () => {}, wheelOpt);
      window.addEventListener('keydown', preventDefaultForScrollKeys, false);
      
      return () => {
        window.removeEventListener('wheel', () => {}, wheelOpt as any);
        window.removeEventListener('touchstart', () => {}, wheelOpt as any);
        window.removeEventListener('touchmove', () => {}, wheelOpt as any);
        window.removeEventListener('keydown', preventDefaultForScrollKeys, false);
      };
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthErrorBoundary>
        <AppAuthProvider>
          {/* ThemeProviderとCssBaselineを追加 */}
          {/* <ThemeProvider theme={theme}>
            <CssBaseline /> */}
            {/* React QueryのProvider */}
            <QueryClientProvider client={queryClient}>
              <AuthGuard> {/* AuthGuardをRoutesの外に移動 */}
                {/* ReportContextのProvider */}
                <ReportProvider>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/auth-callback" element={<LoadingSpinner />} />
                      <Route path="/" element={<Layout />}>
                        <Route index element={<Navigate to="/construction" replace />} />

                        {/* 工事関連のルート */}
                        <Route path="construction">
                          <Route index element={<ConstructionListPage />} />
                          <Route path="new" element={<ConstructionFormPage />} />
                          <Route path=":id/edit" element={<ConstructionFormPage />} />
                        </Route>

                        {/* 日報関連のルート */}
                        <Route path="daily-report">
                          <Route index element={<DailyReportListPage />} />
                          <Route path="new" element={<DailyReportFormPage />} />
                          <Route path=":id/edit" element={<DailyReportFormPage />} />
                        </Route>

                        {/* 請求書作成管理のルート */}
                        <Route path="invoice-request">
                          <Route index element={<InvoiceRequestListPage />} />
                        </Route>

                        {/* スケジュール管理のルート (新規追加) */}
                        <Route path="schedule">
                          <Route index element={<SchedulePage />} />
                        </Route>

                        {/* 売上管理のルート (新規追加) */}
                        <Route path="sales">
                          <Route index element={<SalesPage />} />
                        </Route>

                        {/* 客先検索のルート (新規追加) */}
                        <Route path="customers">
                          <Route index element={<CustomerSearchPage />} />
                        </Route>

                        <Route path="/calculation-sheet/:id" element={<CalculationSheetPage />} />

                        {/* 存在しないパスの場合のリダイレクト */}
                        <Route path="*" element={<Navigate to="/construction" replace />} />
                      </Route>
                    </Routes>
                  </React.Suspense>
                </ReportProvider>
              </AuthGuard>
            </QueryClientProvider>
          {/* </ThemeProvider> */}
        </AppAuthProvider>
      </AuthErrorBoundary>
    </BrowserRouter>
  );
}

export default App;